import {
  ScheduledReport,
  ScheduledReportsResponse,
  CreateScheduledReportRequest,
  UpdateScheduledReportRequest,
  ReportTypesResponse,
  ReportFormat,
  OrganizationUsersResponse,
  RecipientReference
} from '../types/report'
import { PickerContact } from '../types/contact'

export interface GenerateReportParams {
  customerId: string
  reportType: string
  format?: ReportFormat
  period?: string
  customStartDate?: string
  customEndDate?: string
  platformIds?: number[]
  accountIds?: string[]
  postTypes?: string[]
  // Dynamic parameters for specific report types
  partnerId?: string
  activationId?: string
}

// API URL
const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_REPORT_API_URL
  const mode = import.meta.env.MODE

  if (envUrl) {
    return envUrl
  }

  const API_URLS = {
    // TBD - to be removed for security purpose plus having the endpoint only in the env is better
    local: 'http://localhost:3006/api/v1',
    remote: 'https://ath-avi-be-uat-west.azurewebsites.net/api/v1'
  }

  return mode === 'remote' ? API_URLS.remote : API_URLS.local
}

class ReportService {
  private baseURL: string

  constructor() {
    this.baseURL = getApiUrl()
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth-token')
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    const token = this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.message || errorData.error?.message || `HTTP ${response.status}`
      )
    }
    return response.json()
  }

  /**
   * Generate and download a report (PDF or CSV)
   * Uses unified /reports/generate endpoint
   */
  async generateReport(params: GenerateReportParams): Promise<void> {
    const url = `${this.baseURL}/reports/generate`
    const timeoutMs = 180000 // report generation is synchronous server-side (data + Puppeteer setContent 60s + pdf 90s ~= 150s worst case); 30s was too short and surfaced as a connection error. 180s stays above backend max, below Azure ~230s idle cap

    const config: RequestInit = {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(params)
    }

    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(
          new Error(`Report generation timeout after ${timeoutMs / 1000}s`)
        )
      }, timeoutMs)
    })

    try {
      const response = await Promise.race([fetch(url, config), timeoutPromise])

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(
          errorData.message || errorData.error?.message || `HTTP ${response.status}`
        )
      }

      // Get the blob (PDF or CSV) with timeout
      const blobTimeoutMs = 120000 // allow the full generation window for the report body
      const blobTimeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Report download timeout after ${blobTimeoutMs / 1000}s`))
        }, blobTimeoutMs)
      })

      const blob = await Promise.race([response.blob(), blobTimeoutPromise])

      // Validate blob
      if (!blob || blob.size === 0) {
        throw new Error('Downloaded report is empty or invalid')
      }

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('content-disposition')
      const format = params.format || 'pdf'
      let filename = format === 'csv' ? 'post-export.csv' : 'analytics-report.pdf'

      if (contentDisposition) {
        const filenameRegex = /filename\s*=\s*"?([^";\n]+)"?/i
        const match = contentDisposition.match(filenameRegex)
        if (match && match[1]) {
          filename = match[1].trim()
        }
      }

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.style.display = 'none'
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()

      // Cleanup - delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
      }, 100)
    } catch (error) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Failed to generate report')
    }
  }

  /**
   * Get available report types
   * GET /reports/types
   */
  async getReportTypes(): Promise<ReportTypesResponse> {
    const url = `${this.baseURL}/reports/types`
    const timeoutMs = 30000 // 30 second timeout

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])

    return this.handleResponse<ReportTypesResponse>(response)
  }

  /**
   * Get scheduled reports for a customer
   * GET /reports/scheduled
   */
  async getScheduledReports(
    customerId: string,
    page: number = 1,
    limit: number = 10,
    status?: string
  ): Promise<ScheduledReportsResponse> {
    const params = new URLSearchParams({
      customerId,
      page: String(page),
      limit: String(limit)
    })

    if (status) {
      params.append('status', status)
    }

    const url = `${this.baseURL}/reports/scheduled?${params}`
    const timeoutMs = 30000 // 30 second timeout

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])

    return this.handleResponse<ScheduledReportsResponse>(response)
  }

  /**
   * Create a new scheduled report
   * POST /reports/scheduled
   */
  async createScheduledReport(
    data: CreateScheduledReportRequest
  ): Promise<ScheduledReport> {
    const url = `${this.baseURL}/reports/scheduled`

    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    })

    const result = await this.handleResponse<{
      success: boolean
      message: string
      data: ScheduledReport
    }>(response)

    return result.data
  }

  /**
   * Update an existing scheduled report
   * PUT /reports/scheduled/:id
   */
  async updateScheduledReport(
    id: string,
    data: UpdateScheduledReportRequest
  ): Promise<ScheduledReport> {
    const url = `${this.baseURL}/reports/scheduled/${id}`

    const response = await fetch(url, {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(data)
    })

    const result = await this.handleResponse<{
      success: boolean
      message: string
      data: ScheduledReport
    }>(response)

    return result.data
  }

  /**
   * Delete a scheduled report
   * DELETE /reports/scheduled/:id
   */
  async deleteScheduledReport(id: string): Promise<void> {
    const url = `${this.baseURL}/reports/scheduled/${id}`

    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include'
    })

    if (!response.ok && response.status !== 204) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.message || errorData.error?.message || `HTTP ${response.status}`
      )
    }
  }

  /**
   * Toggle schedule status (active/paused)
   * PATCH /reports/scheduled/:id/status
   */
  async toggleScheduleStatus(
    id: string,
    status: 'active' | 'paused'
  ): Promise<ScheduledReport> {
    const url = `${this.baseURL}/reports/scheduled/${id}/status`

    const response = await fetch(url, {
      method: 'PATCH',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ status })
    })

    const result = await this.handleResponse<{
      success: boolean
      message: string
      data: ScheduledReport
    }>(response)

    return result.data
  }

  /**
   * Run a scheduled report immediately
   * POST /reports/scheduled/:id/run-now
   */
  async runScheduleNow(id: string): Promise<void> {
    const url = `${this.baseURL}/reports/scheduled/${id}/run-now`

    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include'
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.message || errorData.error?.message || 'Failed to run schedule'
      )
    }
  }
  /**
   * Get organization users for recipient selection
   * GET /reports/users
   * @param customerId - Optional: fetch users from the organization associated with this customer
   */
  async getOrganizationUsers(customerId?: string): Promise<OrganizationUsersResponse> {
    const params = new URLSearchParams()
    if (customerId) {
      params.append('customerId', customerId)
    }
    const query = params.toString()
    const url = `${this.baseURL}/reports/users${query ? `?${query}` : ''}`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    return this.handleResponse<OrganizationUsersResponse>(response)
  }

  /**
   * Get contacts associated with an activation (through its partner)
   * GET /reports/contacts?activationId=xxx&customerId=xxx
   */
  async getActivationContacts(activationId: string, customerId: string): Promise<PickerContact[]> {
    const params = new URLSearchParams({ activationId, customerId })
    const url = `${this.baseURL}/reports/contacts?${params}`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const result = await this.handleResponse<{ success: boolean; data: { contacts: PickerContact[] } }>(response)
    return result.data.contacts
  }

  /**
   * Update recipients on a scheduled report
   * PATCH /reports/scheduled/:id/recipients
   */
  async updateScheduleRecipients(
    id: string,
    recipientRefs: RecipientReference[]
  ): Promise<ScheduledReport> {
    const url = `${this.baseURL}/reports/scheduled/${id}/recipients`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'PATCH',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ recipientRefs })
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])

    const result = await this.handleResponse<{
      success: boolean
      message: string
      data: ScheduledReport
    }>(response)

    return result.data
  }
}

export const reportService = new ReportService()
